import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    AlertTriangle,
    Award,
    BarChart3,
    CalendarRange,
    CheckCircle2,
    Clock3,
    Download,
    FileSpreadsheet,
    Filter,
    Gauge,
    LayoutDashboard,
    RefreshCcw,
    TrendingUp,
    Users,
    X,
} from "lucide-react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from "recharts";

import { fetchCampagnesRequest } from "../../../redux/actions/campagnesActions";
import { fetchManagerInboxRequest } from "../../../redux/actions/skillEvaluationActions";
import { getDirectReportsRequest } from "../../../redux/actions/userActions";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import "../rf/DashboardSkillsRespo.css";

const COLORS = {
    primary: "#204385",
    secondary: "#ee645f",
    success: "#4caf50",
    warning: "#ff9800",
    error: "#f44336",
    info: "#2196f3",
    grey: "#757575",
};

const CHART_COLORS = [COLORS.primary, COLORS.secondary, COLORS.success, COLORS.warning, COLORS.info, "#9c27b0"];
const DOMAIN_SCORE_COLORS = [COLORS.primary, COLORS.secondary];
const REPARTITION_COLORS = ["#4f7df3", "#48b887", COLORS.secondary, COLORS.primary, COLORS.warning, COLORS.info];

const STATUS_META = {
    NOT_STARTED: { label: "Non demarrée", color: COLORS.secondary },
    EN_ATTENTE_COLLAB: { label: "Attente collab.", color: COLORS.warning },
    EN_ATTENTE_MANAGER: { label: "À traiter", color: COLORS.secondary },
    EN_ATTENTE_N2: { label: "Attente N+2", color: COLORS.info },
    VALIDEE: { label: "Validée", color: COLORS.primary },
    REJETEE: { label: "Rejetée", color: COLORS.error },
};

const numberOrNull = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

const round1 = (value) => Math.round(value * 10) / 10;
const pct = (value, total) => (!total ? 0 : Math.round((value / total) * 100));
const statusCode = (statut) => String(typeof statut === "string" ? statut : statut?.name || "").toUpperCase();

const average = (values) => {
    const nums = (values || []).map(numberOrNull).filter((n) => n !== null);
    if (!nums.length) return null;
    return round1(nums.reduce((sum, n) => sum + n, 0) / nums.length);
};

const getSkillScore = (niveau) =>
    numberOrNull(niveau?.scoreObtenu) ??
    numberOrNull(niveau?.scoreN2) ??
    numberOrNull(niveau?.scoreManager) ??
    numberOrNull(niveau?.scoreCollab);

const getExpectedScore = (niveau) =>
    numberOrNull(niveau?.scoreRequis) ??
    numberOrNull(niveau?.niveauRequis) ??
    numberOrNull(niveau?.niveauAttendu) ??
    numberOrNull(niveau?.scoreAttendu);

const getUserName = (evaluation) =>
    evaluation?.userFullName ||
    evaluation?.fullName ||
    evaluation?.nom ||
    `Collaborateur ${evaluation?.userId || evaluation?.id || ""}`.trim();

function SectionTitle({ icon, children }) {
    return (
        <div className="dsrSectionTitle">
            <span className="dsrSectionTitle__icon">{icon}</span>
            <span>{children}</span>
        </div>
    );
}

function StatCard({ title, value, subtitle, icon, color, onClick }) {
    return (
        <div
            className={`dsrStatCard${onClick ? " dsrStatCard--clickable" : ""}`}
            onClick={onClick}
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={(event) => {
                if (onClick && (event.key === "Enter" || event.key === " ")) onClick();
            }}
        >
            <div className="dsrStatCard__row">
                <div>
                    <div className="dsrStatCard__title">{title}</div>
                    <div className="dsrStatCard__value" style={{ color }}>{value}</div>
                    {subtitle ? <div className="dsrStatCard__sub">{subtitle}</div> : null}
                </div>
                <div className="dsrStatIcon" style={{ color, background: `${color}15`, borderColor: `${color}25` }}>
                    {icon}
                </div>
            </div>
        </div>
    );
}

function DetailsModal({ title, subtitle, children, onClose }) {
    return (
        <div className="dsrModalBackdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
            <div className="dsrModalPanel">
                <div className="dsrModalHeader">
                    <div>
                        <div className="dsrModalTitle">{title}</div>
                        {subtitle ? <div className="dsrModalSubtitle">{subtitle}</div> : null}
                    </div>
                    <button className="dsrModalClose" type="button" onClick={onClose} title="Fermer">
                        <X size={18} />
                    </button>
                </div>
                <div className="dsrModalBody">{children}</div>
            </div>
        </div>
    );
}

function ChartCard({ title, children }) {
    return (
        <div className="dsrChartCard">
            <div className="dsrChartCard__title">{title}</div>
            <div className="dsrChartCard__body">{children}</div>
        </div>
    );
}

// Onglets du tableau de bord
const TABS = [
    { id: "dashboard", label: "Tableau de bord", icon: <LayoutDashboard size={16} /> },
    { id: "etats", label: "États", icon: <FileSpreadsheet size={16} /> },
];

const STATIC_EXCEL_STATES = [
    {
        id: "etat-ecarts",
        title: "État des écarts de compétences",
        description: "Liste des écarts entre niveau requis et niveau obtenu par compétence, pour mon équipe.",
        updatedLabel: "Mis à jour à la demande",
        color: COLORS.error,
    },
    {
        id: "etat-participation",
        title: "État de participation par entité",
        description: "Suivi détaillé du taux de participation et de validation par entité pour mon équipe.",
        updatedLabel: "Mis à jour à la demande",
        color: COLORS.success,
    },
    {
        id: "etat-competences",
        title: "État des scores par compétence et domaine",
        description: "Scores moyens, écarts moyens et répartition par domaine et par compétence pour mon équipe.",
        updatedLabel: "Mis à jour à la demande",
        color: COLORS.primary,
    },
    {
        id: "etat-workflow",
        title: "État du workflow de validation",
        description: "Répartition des évaluations par étape du circuit de validation (auto-éval, manager, N+2) pour mon équipe.",
        updatedLabel: "Mis à jour à la demande",
        color: COLORS.info,
    },
];

function DateFilterFields({ dateDebut, dateFin, onChangeDebut, onChangeFin }) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: "6px 12px",
            }}
        >
            <CalendarRange size={15} color={COLORS.grey} />
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <label htmlFor="dsr-date-debut" style={{ fontSize: 12, color: COLORS.grey }}>Du</label>
                <input
                    id="dsr-date-debut"
                    type="date"
                    value={dateDebut}
                    onChange={(event) => onChangeDebut(event.target.value)}
                    style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: "4px 8px",
                        fontSize: 13,
                        color: "#333",
                    }}
                />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <label htmlFor="dsr-date-fin" style={{ fontSize: 12, color: COLORS.grey }}>Au</label>
                <input
                    id="dsr-date-fin"
                    type="date"
                    value={dateFin}
                    onChange={(event) => onChangeFin(event.target.value)}
                    style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: "4px 8px",
                        fontSize: 13,
                        color: "#333",
                    }}
                />
            </div>
            {(dateDebut || dateFin) && (
                <button
                    type="button"
                    onClick={() => {
                        onChangeDebut("");
                        onChangeFin("");
                    }}
                    style={{
                        background: "#f3f4f6",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: "4px 8px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: COLORS.secondary || "#ee645f",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                    }}
                    title="Réinitialiser les filtres"
                >
                    <X size={12} />
                    Réinitialiser
                </button>
            )}
        </div>
    );
}

function ExcelStateCard({ title, description, updatedLabel, color, onDownload }) {
    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 14,
                padding: 18,
                display: "flex",
                flexDirection: "column",
                gap: 12,
                boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
            }}
        >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div
                    style={{
                        width: 40,
                        height: 40,
                        minWidth: 40,
                        borderRadius: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color,
                        background: `${color}15`,
                        border: `1px solid ${color}25`,
                    }}
                >
                    <FileSpreadsheet size={20} />
                </div>
                <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#1f2937" }}>{title}</div>
                    <div style={{ fontSize: 12.5, color: "#6b7280", marginTop: 4, lineHeight: 1.4 }}>
                        {description}
                    </div>
                </div>
            </div>

            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginTop: "auto",
                    paddingTop: 8,
                    borderTop: "1px dashed #eef0f2",
                }}
            >
                <span style={{ fontSize: 11.5, color: "#9ca3af", display: "flex", alignItems: "center", gap: 5 }}>
                    <Clock3 size={12} /> {updatedLabel}
                </span>
                <button
                    type="button"
                    title="Télécharger l'état Excel"
                    onClick={onDownload}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        border: "none",
                        borderRadius: 8,
                        padding: "7px 12px",
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: "#fff",
                        background: color,
                        cursor: "pointer",
                    }}
                >
                    <Download size={14} />
                    Télécharger
                </button>
            </div>
        </div>
    );
}

export default function DashboardSkillsManager() {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth || {});
    const { list: campagnesRaw } = useSelector((state) => state.campagnes || { list: [] });
    const { inboxManager = [], loading } = useSelector((state) => state.skillEvaluation || {});
    const { directReports = [] } = useSelector((state) => state.user || {});

    const [campagneId, setCampagneId] = useState("");
    const [detailsModal, setDetailsModal] = useState(null);
    const [activeTab, setActiveTab] = useState("dashboard");
    const [dateDebut, setDateDebut] = useState("");
    const [dateFin, setDateFin] = useState("");

    useEffect(() => {
        dispatch(fetchCampagnesRequest());
        dispatch(fetchManagerInboxRequest());
        if (user?.id || user?.uid) dispatch(getDirectReportsRequest(user.id || user.uid));
    }, [dispatch, user?.id, user?.uid]);

    const campagnes = useMemo(() => {
        return (campagnesRaw || [])
            .filter((c) => String(c?.typeCampagne || "").toUpperCase() === "SKILLS" || !c?.typeCampagne)
            .sort((a, b) => (a?.status === b?.status ? 0 : a?.status === true ? -1 : 1));
    }, [campagnesRaw]);

    useEffect(() => {
        if (!campagnes.length) {
            setCampagneId("");
            return;
        }
        if (!campagneId || !campagnes.some((campagne) => String(campagne.id) === String(campagneId))) {
            const defaultCampagne = campagnes.find((campagne) => campagne?.isDefault) || campagnes.find((campagne) => campagne?.status === true) || campagnes[0];
            setCampagneId(String(defaultCampagne?.id || ""));
        }
    }, [campagnes, campagneId]);

    const refreshData = useCallback(() => {
        dispatch(fetchManagerInboxRequest());
    }, [dispatch]);

    const inboxManagerFiltered = useMemo(() => {
        return (inboxManager || []).filter((evaluation) => {
            const evalDateStr = evaluation.dateEvaluation || evaluation.createdAt;
            if (!evalDateStr) return true;
            const evalDate = new Date(evalDateStr);
            if (dateDebut) {
                const start = new Date(dateDebut);
                start.setHours(0, 0, 0, 0);
                if (evalDate < start) return false;
            }
            if (dateFin) {
                const end = new Date(dateFin);
                end.setHours(23, 59, 59, 999);
                if (evalDate > end) return false;
            }
            return true;
        });
    }, [inboxManager, dateDebut, dateFin]);

    const teamRows = useMemo(() => {
        const evaluations = (inboxManagerFiltered || []).filter((evaluation) => !campagneId || Number(evaluation.campagneId) === Number(campagneId));
        const reportsById = new Map((directReports || []).map((report) => [report.id, report]));

        const reportRows = (directReports || []).map((report) => {
            const evaluation = evaluations.find((item) => Number(item.userId) === Number(report.id));
            return {
                ...evaluation,
                userId: report.id,
                userFullName: report.fullName || evaluation?.userFullName,
                jobName: report.jobName || evaluation?.jobName,
                statut: evaluation?.statut || "NOT_STARTED",
                niveaux: evaluation?.niveaux || [],
                hasEvaluation: !!evaluation,
            };
        });

        const extraRows = evaluations
            .filter((evaluation) => !reportsById.has(evaluation.userId))
            .map((evaluation) => ({ ...evaluation, hasEvaluation: true }));

        return reportRows.length ? [...reportRows, ...extraRows] : evaluations;
    }, [inboxManager, directReports, campagneId]);

    const analytics = useMemo(() => {
        const totalTeam = Math.max(teamRows.length, directReports.length);
        const evaluations = teamRows.filter((row) => row.hasEvaluation !== false);
        const statusCounts = teamRows.reduce((acc, row) => {
            const code = statusCode(row.statut) || "NOT_STARTED";
            acc[code] = (acc[code] || 0) + 1;
            return acc;
        }, {});

        const allLevels = evaluations.flatMap((evaluation) =>
            (evaluation.niveaux || []).map((niveau) => ({ ...niveau, evaluation }))
        );
        const scoredLevels = allLevels
            .map((item) => ({ ...item, score: getSkillScore(item), expected: getExpectedScore(item) }))
            .filter((item) => item.score !== null);
        const gaps = scoredLevels
            .filter((item) => item.expected !== null)
            .map((item) => ({ ...item, gap: item.expected - item.score }));

        const avgScore = average(scoredLevels.map((item) => item.score));
        const avgGap = average(gaps.map((item) => item.gap));
        const criticalGaps = gaps.filter((item) => item.gap >= 1).length;
        const teamDetails = teamRows
            .map((row) => {
                const levels = (row.niveaux || [])
                    .map((niveau) => ({ score: getSkillScore(niveau), expected: getExpectedScore(niveau) }))
                    .filter((niveau) => niveau.score !== null);
                const rowGaps = levels.filter((niveau) => niveau.expected !== null).map((niveau) => niveau.expected - niveau.score);
                return {
                    collaborator: getUserName(row),
                    job: row.jobName || row.poste || row.userEmployeeCode || "-",
                    status: statusCode(row.statut) || "NOT_STARTED",
                    score: average(levels.map((niveau) => niveau.score)),
                    gap: average(rowGaps),
                    skillsCount: row.niveaux?.length || 0,
                    hasEvaluation: row.hasEvaluation !== false,
                };
            })
            .sort((a, b) => a.collaborator.localeCompare(b.collaborator));
        const toReviewDetails = teamDetails.filter((row) => row.status === "EN_ATTENTE_MANAGER");
        const criticalGapDetails = gaps
            .filter((item) => item.gap >= 1)
            .map((item) => ({
                collaborator: getUserName(item.evaluation),
                job: item.evaluation?.jobName || item.evaluation?.poste || item.evaluation?.userEmployeeCode || "-",
                skill: item.skillNom || item.skillCode || "Compétence",
                domain: item.domaineNom || item.domaineName || "-",
                expected: item.expected,
                score: item.score,
                gap: item.gap,
                status: statusCode(item.evaluation?.statut),
            }))
            .sort((a, b) => b.gap - a.gap || a.collaborator.localeCompare(b.collaborator));

        const statusData = Object.entries(statusCounts).map(([code, value]) => ({
            name: STATUS_META[code]?.label || code,
            value,
            color: STATUS_META[code]?.color || COLORS.grey,
        }));

        const collaboratorScores = teamRows.map((row) => {
            const levels = (row.niveaux || [])
                .map((niveau) => ({ score: getSkillScore(niveau), expected: getExpectedScore(niveau) }))
                .filter((niveau) => niveau.score !== null);
            const rowGaps = levels.filter((niveau) => niveau.expected !== null).map((niveau) => niveau.expected - niveau.score);
            return {
                name: getUserName(row),
                score: average(levels.map((niveau) => niveau.score)) || 0,
                gap: average(rowGaps) || 0,
                status: statusCode(row.statut),
            };
        });

        const byDomain = Object.values(
            scoredLevels.reduce((acc, item) => {
                const name = item.domaineNom || item.domaineName || "Non classe";
                if (!acc[name]) acc[name] = { name, scores: [], gaps: [] };
                acc[name].scores.push(item.score);
                if (item.expected !== null) acc[name].gaps.push(item.expected - item.score);
                return acc;
            }, {})
        )
            .map((item) => ({ name: item.name, score: average(item.scores) || 0, gap: average(item.gaps) || 0 }))
            .sort((a, b) => b.gap - a.gap)
            .slice(0, 8);

        const skills = Object.values(
            scoredLevels.reduce((acc, item) => {
                const key = item.skillId || item.skillNom || item.skillCode;
                const name = item.skillNom || item.skillCode || "Compétence";
                if (!acc[key]) acc[key] = { name, scores: [], gaps: [], count: 0 };
                acc[key].scores.push(item.score);
                if (item.expected !== null) acc[key].gaps.push(item.expected - item.score);
                acc[key].count += 1;
                return acc;
            }, {})
        ).map((item) => ({ name: item.name, score: average(item.scores) || 0, gap: average(item.gaps) || 0, count: item.count }));

        return {
            totalTeam,
            totalEvaluations: evaluations.length,
            submitted: Math.max(totalTeam - (statusCounts.NOT_STARTED || 0) - (statusCounts.EN_ATTENTE_COLLAB || 0), 0),
            toReview: statusCounts.EN_ATTENTE_MANAGER || 0,
            waitingN2: statusCounts.EN_ATTENTE_N2 || 0,
            validated: statusCounts.VALIDEE || 0,
            completionRate: pct(statusCounts.VALIDEE || 0, totalTeam),
            avgScore,
            avgGap,
            criticalGaps,
            teamDetails,
            toReviewDetails,
            criticalGapDetails,
            statusData,
            collaboratorScores,
            byDomain,
            criticalSkills: [...skills].filter((skill) => skill.gap > 0).sort((a, b) => b.gap - a.gap).slice(0, 5),
            strongSkills: [...skills].sort((a, b) => b.score - a.score).slice(0, 5),
        };
    }, [teamRows, directReports.length]);

    const handleDownloadExcel = (stateId) => {
        const evaluationsFiltered = teamRows.filter((row) => row.hasEvaluation !== false);
        if (!evaluationsFiltered.length) {
            Swal.fire({
                icon: "warning",
                title: "Aucune donnée",
                text: "Aucune donnée disponible pour la période sélectionnée.",
                confirmButtonColor: COLORS.primary || "#204385"
            });
            return;
        }

        const currentCampagne = campagnes.find(c => String(c.id) === String(campagneId));
        const campagneNom = currentCampagne ? currentCampagne.nom || currentCampagne.name : `Campagne_${campagneId}`;
        const prefix = `${campagneNom.replace(/\s+/g, "_")}`;

        if (stateId === "etat-ecarts") {
            const allLevels = evaluationsFiltered.flatMap((evaluation) =>
                (evaluation.niveaux || []).map((niveau) => ({ ...niveau, evaluation }))
            );
            const rows = allLevels.map((item) => {
                const score = getSkillScore(item);
                const expected = getExpectedScore(item);
                const gap = (expected !== null && score !== null) ? expected - score : null;
                return {
                    "Collaborateur": getUserName(item.evaluation),
                    "Poste": item.evaluation?.jobName || item.evaluation?.poste || "-",
                    "Domaine": item.domaineNom || item.domaineName || "-",
                    "Compétence": item.skillNom || item.skillCode || "-",
                    "Niveau Requis": expected ?? "-",
                    "Niveau Obtenu": score ?? "-",
                    "Écart": gap ?? "-",
                    "Statut Évaluation": STATUS_META[statusCode(item.evaluation?.statut)]?.label || item.evaluation?.statut || "-"
                };
            });
            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Écarts de compétences");
            XLSX.writeFile(workbook, `${prefix}_ecarts_competences.xlsx`);
        } else if (stateId === "etat-participation") {
            const entityCounts = evaluationsFiltered.reduce((acc, evaluation) => {
                const name = evaluation.entityName || evaluation.entiteName || "Non renseignée";
                if (!acc[name]) acc[name] = { total: 0, validated: 0 };
                acc[name].total += 1;
                if (statusCode(evaluation.statut) === "VALIDEE") acc[name].validated += 1;
                return acc;
            }, {});

            const rows = Object.entries(entityCounts).map(([name, data]) => ({
                "Entité": name,
                "Total Collaborateurs": data.total,
                "Validés": data.validated,
                "Taux de Validation (%)": pct(data.validated, data.total)
            }));

            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Participation");
            XLSX.writeFile(workbook, `${prefix}_participation_entites.xlsx`);
        } else if (stateId === "etat-competences") {
            const allLevels = evaluationsFiltered.flatMap((evaluation) =>
                (evaluation.niveaux || []).map((niveau) => ({ ...niveau, evaluation }))
            );
            const scoredLevels = allLevels
                .map((item) => ({
                    ...item,
                    score: getSkillScore(item),
                    expected: getExpectedScore(item),
                }))
                .filter((item) => item.score !== null);

            // Par Domaine
            const domainData = Object.values(
                scoredLevels.reduce((acc, item) => {
                    const name = item.domaineNom || item.domaineName || "Non classé";
                    if (!acc[name]) acc[name] = { name, scores: [], gaps: [] };
                    acc[name].scores.push(item.score);
                    if (item.expected !== null) acc[name].gaps.push(item.expected - item.score);
                    return acc;
                }, {})
            ).map((item) => ({
                "Domaine": item.name,
                "Score Moyen": average(item.scores) || 0,
                "Écart Moyen": average(item.gaps) || 0,
                "Nombre d'évaluations": item.scores.length
            }));

            // Par Compétence
            const skillData = Object.values(
                scoredLevels.reduce((acc, item) => {
                    const name = item.skillNom || item.skillCode || "Compétence";
                    if (!acc[name]) acc[name] = { name, scores: [], gaps: [] };
                    acc[name].scores.push(item.score);
                    if (item.expected !== null) acc[name].gaps.push(item.expected - item.score);
                    return acc;
                }, {})
            ).map((item) => ({
                "Compétence": item.name,
                "Score Moyen": average(item.scores) || 0,
                "Écart Moyen": average(item.gaps) || 0,
                "Nombre d'évaluations": item.scores.length
            }));

            const workbook = XLSX.utils.book_new();
            const wsDomain = XLSX.utils.json_to_sheet(domainData);
            const wsSkill = XLSX.utils.json_to_sheet(skillData);
            XLSX.utils.book_append_sheet(workbook, wsDomain, "Par Domaine");
            XLSX.utils.book_append_sheet(workbook, wsSkill, "Par Compétence");
            XLSX.writeFile(workbook, `${prefix}_scores_competences.xlsx`);
        } else if (stateId === "etat-workflow") {
            const rows = evaluationsFiltered.map((evaluation) => ({
                "Collaborateur": getUserName(evaluation),
                "Poste": evaluation.jobName || evaluation.poste || "-",
                "Statut": STATUS_META[statusCode(evaluation.statut)]?.label || evaluation.statut || "-",
                "Date Évaluation": evaluation.dateEvaluation ? new Date(evaluation.dateEvaluation).toLocaleDateString() : "-",
                "Décision par (Manager)": evaluation.managerDecisionByName || "-",
                "Date Décision (Manager)": evaluation.managerDecisionAt ? new Date(evaluation.managerDecisionAt).toLocaleDateString() : "-",
                "Commentaire (Manager)": evaluation.managerComment || "-",
                "Décision par (N+2)": evaluation.n2DecisionByName || "-",
                "Date Décision (N+2)": evaluation.n2DecisionAt ? new Date(evaluation.n2DecisionAt).toLocaleDateString() : "-",
                "Commentaire (N+2)": evaluation.n2Comment || "-"
            }));

            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Workflow de validation");
            XLSX.writeFile(workbook, `${prefix}_workflow_validation.xlsx`);
        }
    };


    return (
        <div className="dsrPage">
            <header className="dsrHeader">
                <div>
                    <h1>Tableau de bord de mon équipe</h1>
                </div>
                <div className="dsrActions" style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                    <DateFilterFields
                        dateDebut={dateDebut}
                        dateFin={dateFin}
                        onChangeDebut={setDateDebut}
                        onChangeFin={setDateFin}
                    />

                    <div className="dsrSelectWrap">
                        <Filter size={15}/>
                        <label htmlFor="dsm-campagne">Campagne</label>
                        <select id="dsm-campagne" className="dsrSelect" value={campagneId}
                                onChange={(event) => setCampagneId(event.target.value)}>
                            <option value="">Toutes les campagnes</option>
                            {campagnes.map((campagne) => (
                                <option key={campagne.id}
                                        value={campagne.id}>{campagne.nom || campagne.name || `Campagne ${campagne.id}`}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </header>

            {/* Navigation par onglets */}
            <nav
                style={{
                    display: "flex",
                    gap: 6,
                    borderBottom: "1px solid #e5e7eb",
                    margin: "4px 0 18px",
                }}
            >
                {TABS.map((tab) => {
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 7,
                                padding: "9px 16px",
                                fontSize: 13.5,
                                fontWeight: 600,
                                border: "none",
                                borderBottom: isActive ? `2px solid ${COLORS.primary}` : "2px solid transparent",
                                background: "transparent",
                                color: isActive ? COLORS.primary : "#6b7280",
                                cursor: "pointer",
                                marginBottom: -1,
                            }}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    );
                })}
            </nav>

            {activeTab === "dashboard" && (
                <main className="dsrMain">
                    <SectionTitle icon={<Gauge size={18} />}>Pilotage de l'équipe</SectionTitle>
                    <div className="dsrGrid dsrGrid--4">
                        <StatCard title="Collaborateurs" value={analytics.totalTeam} icon={<Users size={20} />} color={COLORS.primary} onClick={() => setDetailsModal("team")} />
                        <StatCard title="À traiter" value={analytics.toReview}  icon={<Clock3 size={20} />} color={COLORS.secondary} onClick={() => setDetailsModal("to-review")} />
                        <StatCard title="Écarts critiques" value={analytics.criticalGaps} icon={<AlertTriangle size={20} />} color={COLORS.error} onClick={() => setDetailsModal("critical-gaps")} />
                        <StatCard title="Score moyen de l'équipe" value={analytics.avgScore ?? "-"} icon={<Award size={20} />} color={COLORS.info} />
                    </div>

                    <SectionTitle icon={<BarChart3 size={18} />}>Analyse de l'équipe</SectionTitle>
                    <div className="dsrGrid dsrGrid--2">
                        <ChartCard title="Répartition des statuts">
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie data={analytics.statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={92} label={({ name, value }) => `${name}: ${value}`}>
                                        {analytics.statusData.map((entry, index) => <Cell key={entry.name} fill={REPARTITION_COLORS[index % REPARTITION_COLORS.length]} />)}
                                    </Pie>
                                    <RechartsTooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </ChartCard>

                        <ChartCard title="Score moyen par domaine">
                            <ResponsiveContainer width="100%" height={320}>
                                <BarChart data={analytics.byDomain}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="name" interval={0} tick={{ fontSize: 11 }} />
                                    <YAxis domain={[0, 4]} />
                                    <RechartsTooltip />
                                    <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                                        {analytics.byDomain.map((entry, index) => (
                                            <Cell key={entry.name} fill={DOMAIN_SCORE_COLORS[index % DOMAIN_SCORE_COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    </div>

                    <SectionTitle icon={<AlertTriangle size={18} />}>Actions prioritaires</SectionTitle>
                    <div className="dsrGrid dsrGrid--2">
                        <div className="dsrPanel">
                            <div className="dsrPanel__title">Compétences à renforcer</div>
                            <div className="dsrPanel__body">
                                {analytics.criticalSkills.length ? (
                                    <div className="dsrRiskList">
                                        {analytics.criticalSkills.map((skill) => (
                                            <div className="dsrRiskItem" key={skill.name}>
                                                <div>
                                                    <div className="dsrRiskTitle">{skill.name}</div>
                                                    <div className="dsrRiskMeta">{skill.count} evaluations - score moyen {skill.score}</div>
                                                </div>
                                                <span className="dsrBadge dsrBadge--danger">+{skill.gap}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : <div className="dsrEmpty">Aucun écart prioritaire</div>}
                            </div>
                        </div>

                        <div className="dsrPanel">
                            <div className="dsrPanel__title">Forces de l'équipe</div>
                            <div className="dsrPanel__body">
                                {analytics.strongSkills.length ? (
                                    <div className="dsrRiskList">
                                        {analytics.strongSkills.map((skill) => (
                                            <div className="dsrRiskItem" key={skill.name}>
                                                <div>
                                                    <div className="dsrRiskTitle">{skill.name}</div>
                                                    <div className="dsrRiskMeta">{skill.count} evaluations - écart moyen {skill.gap}</div>
                                                </div>
                                                <span className="dsrBadge dsrBadge--success">{skill.score}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : <div className="dsrEmpty">Aucune donnee disponible</div>}
                            </div>
                        </div>
                    </div>
                </main>
            )}

            {activeTab === "etats" && (
                <main className="dsrMain">
                    <SectionTitle icon={<FileSpreadsheet size={18} />}>États à télécharger</SectionTitle>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                            gap: 16,
                        }}
                    >
                        {STATIC_EXCEL_STATES.map((state) => (
                            <ExcelStateCard
                                key={state.id}
                                title={state.title}
                                description={state.description}
                                updatedLabel={state.updatedLabel}
                                color={state.color}
                                onDownload={() => handleDownloadExcel(state.id)}
                            />
                        ))}
                    </div>
                </main>
            )}

            {detailsModal === "team" && (
                <DetailsModal
                    title="Collaborateurs de mon équipe"
                    subtitle={`${analytics.teamDetails.length} collaborateur(s) pour la campagne sélectionnée`}
                    onClose={() => setDetailsModal(null)}
                >
                    {analytics.teamDetails.length ? (
                        <div className="dsrTableWrap">
                            <table className="dsrTable">
                                <thead>
                                <tr>
                                    <th>Collaborateur</th>
                                    <th>Poste</th>
                                    <th>Statut</th>
                                    <th>Compétences</th>
                                    <th>Score moyen</th>
                                    <th>Écart moyen</th>
                                </tr>
                                </thead>
                                <tbody>
                                {analytics.teamDetails.map((row, index) => (
                                    <tr key={`${row.collaborator}-${index}`}>
                                        <td>{row.collaborator}</td>
                                        <td>{row.job}</td>
                                        <td>{STATUS_META[row.status]?.label || row.status || "-"}</td>
                                        <td>{row.skillsCount}</td>
                                        <td>{row.score ?? "-"}</td>
                                        <td>{row.gap ?? "-"}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="dsrEmpty">Aucun collaborateur a afficher.</div>
                    )}
                </DetailsModal>
            )}

            {detailsModal === "to-review" && (
                <DetailsModal
                    title="Collaborateurs à traiter"
                    onClose={() => setDetailsModal(null)}
                >
                    {analytics.toReviewDetails.length ? (
                        <div className="dsrTableWrap">
                            <table className="dsrTable">
                                <thead>
                                <tr>
                                    <th>Collaborateur</th>
                                    <th>Poste</th>
                                    <th>Compétences</th>
                                    <th>Score moyen</th>
                                    <th>Écart moyen</th>
                                    <th>Action attendue</th>
                                </tr>
                                </thead>
                                <tbody>
                                {analytics.toReviewDetails.map((row, index) => (
                                    <tr key={`${row.collaborator}-${index}`}>
                                        <td>{row.collaborator}</td>
                                        <td>{row.job}</td>
                                        <td>{row.skillsCount}</td>
                                        <td>{row.score ?? "-"}</td>
                                        <td>{row.gap ?? "-"}</td>
                                        <td><span className="dsrBadge dsrBadge--warning">Valider</span></td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="dsrEmpty">Aucune évaluation en attente de traitement.</div>
                    )}
                </DetailsModal>
            )}

            {detailsModal === "critical-gaps" && (
                <DetailsModal
                    title="Détails des écarts critiques de l'équipe"
                    subtitle={`${analytics.criticalGapDetails.length} compétence(s) avec un écart superieur ou egal a 1 niveau`}
                    onClose={() => setDetailsModal(null)}
                >
                    {analytics.criticalGapDetails.length ? (
                        <div className="dsrTableWrap">
                            <table className="dsrTable">
                                <thead>
                                <tr>
                                    <th>Collaborateur</th>
                                    <th>Poste</th>
                                    <th>Compétence</th>
                                    <th>Domaine</th>
                                    <th>Requis</th>
                                    <th>Obtenu</th>
                                    <th>Écart</th>
                                    <th>Statut</th>
                                </tr>
                                </thead>
                                <tbody>
                                {analytics.criticalGapDetails.map((row, index) => (
                                    <tr key={`${row.collaborator}-${row.skill}-${index}`}>
                                        <td>{row.collaborator}</td>
                                        <td>{row.job}</td>
                                        <td>{row.skill}</td>
                                        <td>{row.domain}</td>
                                        <td>{row.expected}</td>
                                        <td>{row.score}</td>
                                        <td><span className="dsrBadge dsrBadge--danger">+{row.gap}</span></td>
                                        <td>{STATUS_META[row.status]?.label || row.status || "-"}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="dsrEmpty">Aucun écart critique pour cette équipe.</div>
                    )}
                </DetailsModal>
            )}
        </div>
    );
}
